import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  InputAdornment,
  CircularProgress
} from '@mui/material';

import {
  Email,
  VpnKey,
  CheckCircle,
  ArrowForward
} from '@mui/icons-material';

const url_verify = (process.env.REACT_APP_API_BASEURL || '') + '/api/verify';

const VerifyForm = () => {
  const [email, setEmail] = useState('');
  const [secretToken, setSecretToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  const history = useHistory();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !secretToken) {
      toast.error('Please fill in all fields.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(url_verify, {
        email: email,
        secretToken: secretToken
      });

      if (response.data.message === 'Not authorized to use this!') {
        toast.error('Log in first!');
      } else {
        toast.success('Email verified successfully! Redirecting to Sign In...');
        setTimeout(() => history.push('/sign-in'), 3000);
      }
    } catch (err) {
      const errorMsg = err.response?.data || err.message;
      
      if (errorMsg === 'User not in database') {
        toast.error('User not found. Please check your email address.');
      } else if (errorMsg === 'Your token is invalid. Try again.') {
        toast.error('Invalid token. Please check and try again.');
      } else {
        toast.error(errorMsg || 'Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            borderRadius: 2,
            width: '100%',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          {/* Icon */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckCircle sx={{ fontSize: 32, color: 'white' }} />
            </Box>
          </Box>

          <Typography component="h1" variant="h4" align="center" sx={{ mb: 1 }}>
            Verify Account
          </Typography>

          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 4, color: 'text.secondary' }}
          >
            Enter your email and the verification token sent to your inbox.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email color="action" />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="secretToken"
              label="Verification Token"
              name="secretToken"
              autoComplete="off"
              value={secretToken}
              onChange={(e) => setSecretToken(e.target.value)}
              placeholder="Paste your token here"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <VpnKey color="action" />
                  </InputAdornment>
                )
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={isLoading}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(90deg, #3f51b5 0%, #2196f3 100%)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)'
                }
              }}
              endIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
            >
              {isLoading ? 'Verifying...' : 'Verify Account'}
            </Button>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography
                component="a"
                href="#/sign-in"
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Already verified? Sign in
              </Typography>
            </Box>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography
                component="a"
                href="#/sign-up"
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Don't have an account? Sign up
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyForm;